import { useState, useEffect, useRef } from 'react';
import environmentConfig from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const useWebSocket = (url, options = {}) => {
  const [lastMessage, setLastMessage] = useState(null);
  const [readyState, setReadyState] = useState(0);
  const [connectionError, setConnectionError] = useState(null);
  const socketRef = useRef(null);
  const { user } = useAuth();
  const reconnectTimeoutRef = useRef(null);

  const wsUrl = url || (user?.tenant_id ? `${environmentConfig.WS_URL}?tenantId=${user.tenant_id}` : environmentConfig.WS_URL);

  useEffect(() => {
    // Só conecta se tiver URL e usuário logado
    if (!wsUrl || !user) {
      setConnectionError('Missing WebSocket URL or user not authenticated');
      return;
    }
    
    // Validate WebSocket URL format
    if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
      setConnectionError('Invalid WebSocket URL format');
      return;
    }
    setConnectionError(null);
    setReadyState(0); // CONNECTING
    
    const ws = new WebSocket(wsUrl);
    
    // Set connection timeout
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.close();
        setConnectionError('Connection timeout');
        setReadyState(3);
      }
    }, 10000); // 10 second timeout
    
    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      setReadyState(1);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
        options.onMessage?.(data);
      } catch (error) {
        // Silent error handling
      }
    };

    ws.onerror = (error) => {
      setConnectionError(`WebSocket connection failed: ${error.message || 'Unknown error'}`);
      setReadyState(3);
    };
    
    ws.onclose = (event) => {
      if (event.code !== 1000) {
        setConnectionError(`WebSocket closed unexpectedly: ${event.code} - ${event.reason}`);
      }
      setReadyState(3);
    };

    socketRef.current = ws;

    return () => {
      clearTimeout(connectionTimeout);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [wsUrl, user]);

  const sendMessage = (message) => {
    if (socketRef.current && readyState === 1) {
      socketRef.current.send(JSON.stringify(message));
    }
  };

  return {
    lastMessage,
    readyState,
    sendMessage,
    connectionError
  };
};

export default useWebSocket;